import { DownloadSimple, FilePdf, Fire, Snowflake, MoonStars } from "@phosphor-icons/react";
import { reportPdfUrl, reportCsvUrl } from "@/lib/api";

const SLOTS = ["07:00", "10:00", "15:00", "19:00", "21:00"];
const SLOT_LABELS = {
    "07:00": "7 AM",
    "10:00": "10 AM",
    "15:00": "3 PM",
    "19:00": "7 PM",
    "21:00": "9 PM",
};

const heatColor = (count, max) => {
    if (max === 0 || count === 0) return "bg-neutral-100 text-neutral-500";
    const ratio = count / max;
    if (ratio >= 0.8) return "bg-red-600 text-white";
    if (ratio >= 0.5) return "bg-orange-500 text-white";
    if (ratio >= 0.25) return "bg-amber-300 text-ink";
    return "bg-emerald-200 text-ink";
};

export const DailyReportPanel = ({ report }) => {
    if (!report) return null;
    const maxHeat = Math.max(...SLOTS.map((s) => report.heatmap?.[s] || 0), 0);
    const recommended = new Set(report.recommended_slots || []);

    return (
        <div data-testid="daily-report-panel" className="space-y-6">
            {/* Header */}
            <div className="flex flex-wrap items-start justify-between gap-3 border-b-2 border-ink pb-3">
                <div className="min-w-0">
                    <h2 className="font-display text-2xl font-black tracking-tighter text-ink">
                        Daily Opportunity Report
                    </h2>
                    <p className="mono-label mt-1">
                        {report.business_type} · {report.city} · {report.report_date} · {report.timezone}
                    </p>
                </div>
                <div className="flex gap-2">
                    <a
                        data-testid="report-download-csv"
                        href={reportCsvUrl(report.id)}
                        className="brutal-btn bg-white border border-ink text-ink font-mono uppercase tracking-widest text-[10px] font-bold px-3 h-9 flex items-center gap-1.5"
                    >
                        <DownloadSimple size={13} weight="bold" />
                        CSV
                    </a>
                    <a
                        data-testid="report-download-pdf"
                        href={reportPdfUrl(report.id)}
                        className="brutal-btn bg-ink border border-ink text-white font-mono uppercase tracking-widest text-[10px] font-bold px-3 h-9 flex items-center gap-1.5"
                    >
                        <FilePdf size={13} weight="bold" />
                        PDF
                    </a>
                </div>
            </div>

            {/* Heatmap */}
            <section>
                <div className="mono-label mb-2 flex items-center gap-1.5">
                    Competition Heatmap
                    <span className="text-neutral-400 normal-case">
                        · {report.unique_business_count} unique advertiser{report.unique_business_count === 1 ? "" : "s"}
                    </span>
                </div>
                <div data-testid="report-heatmap" className="grid grid-cols-5 gap-2">
                    {SLOTS.map((s) => {
                        const count = report.heatmap?.[s] || 0;
                        const isMost = s === report.most_crowded_slot && count > 0;
                        const isLeast = s === report.least_crowded_slot && count > 0;
                        return (
                            <div
                                key={s}
                                data-testid={`heatmap-${s}`}
                                className={`border-2 border-ink p-3 flex flex-col items-center justify-center text-center ${heatColor(count, maxHeat)}`}
                            >
                                <div className="font-heading text-3xl font-black leading-none">{count}</div>
                                <div className="mono-label mt-1">{SLOT_LABELS[s]}</div>
                                <div className="mt-1.5 h-4">
                                    {isMost && (
                                        <span className="inline-flex items-center gap-0.5 text-[9px] font-bold uppercase tracking-widest">
                                            <Fire size={9} weight="bold" /> Peak
                                        </span>
                                    )}
                                    {isLeast && (
                                        <span className="inline-flex items-center gap-0.5 text-[9px] font-bold uppercase tracking-widest">
                                            <Snowflake size={9} weight="bold" /> Coolest
                                        </span>
                                    )}
                                </div>
                            </div>
                        );
                    })}
                </div>
            </section>

            {/* Timeline table */}
            <section>
                <div className="mono-label mb-2">Timeline · Who Advertised When</div>
                {report.timeline?.length ? (
                    <div className="border-2 border-ink overflow-x-auto">
                        <table data-testid="report-timeline" className="w-full text-sm">
                            <thead>
                                <tr className="bg-ink text-white">
                                    <th className="text-left px-3 py-2 font-mono uppercase text-[10px] tracking-widest">
                                        Business
                                    </th>
                                    {SLOTS.map((s) => (
                                        <th
                                            key={s}
                                            className={`px-2 py-2 font-mono uppercase text-[10px] tracking-widest ${
                                                recommended.has(s) ? "bg-klein" : ""
                                            }`}
                                            title={recommended.has(s) ? "Recommended slot (lowest CPC opportunity)" : ""}
                                        >
                                            {SLOT_LABELS[s]}
                                        </th>
                                    ))}
                                    <th className="px-2 py-2 font-mono uppercase text-[10px] tracking-widest">#</th>
                                </tr>
                            </thead>
                            <tbody>
                                {report.timeline.map((row, i) => (
                                    <tr key={row.business} className={i % 2 ? "bg-neutral-50" : "bg-white"}>
                                        <td className="px-3 py-2 font-mono text-xs text-ink truncate max-w-[220px]">
                                            {row.business}
                                        </td>
                                        {row.slots_present.map((p, si) => (
                                            <td key={si} className="px-2 py-2 text-center">
                                                {p ? (
                                                    <span className="inline-block h-3 w-3 rounded-full bg-klein" />
                                                ) : (
                                                    <span className="inline-block h-3 w-3 rounded-full bg-neutral-200" />
                                                )}
                                            </td>
                                        ))}
                                        <td className="px-2 py-2 text-center font-mono text-xs font-bold">
                                            {row.presence_count}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                ) : (
                    <p className="text-sm text-neutral-600">
                        No advertiser presence recorded across the 5 slots today.
                    </p>
                )}
            </section>

            {/* Late-night analysis + Recommendation */}
            <div className="grid gap-4 md:grid-cols-2">
                <section className="border-2 border-ink bg-neutral-50 p-4">
                    <div className="mono-label mb-2 flex items-center gap-1.5">
                        <MoonStars size={13} weight="bold" />
                        Late-Night Opportunity (7 PM &amp; 9 PM)
                    </div>
                    <p
                        data-testid="report-late-night"
                        className="text-sm leading-relaxed text-neutral-800"
                    >
                        {report.late_night_analysis || "n/a"}
                    </p>
                </section>
                <section className="border-2 border-klein bg-klein/5 p-4">
                    <div className="mono-label mb-2 text-klein">Recommendation</div>
                    <p
                        data-testid="report-recommendation"
                        className="text-sm leading-relaxed text-ink"
                    >
                        {report.recommendation || "n/a"}
                    </p>
                    {report.recommended_slots?.length > 0 && (
                        <div className="mt-3 flex flex-wrap gap-1.5">
                            {report.recommended_slots.map((s) => (
                                <span
                                    key={s}
                                    data-testid={`report-rec-slot-${s}`}
                                    className="border-2 border-klein bg-white text-klein px-2 py-0.5 font-mono text-xs font-bold"
                                >
                                    Bid at {SLOT_LABELS[s]}
                                </span>
                            ))}
                        </div>
                    )}
                </section>
            </div>
        </div>
    );
};
